import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { DatePicker, Drawer, Form, Select, Switch } from 'antd'
import dayjs from 'dayjs'
import { useBaseFreightDrawer } from '../hooks'
import type { BaseFreightGroup, Ratebook, Region } from '@/api/useFreight/types'
export function CreateBaseFreightDrawer(props: {
  ratebook?: Ratebook
  visible: boolean
  onClose: () => void
  onSuccess: (created: BaseFreightGroup) => void
  editingBaseFreight: BaseFreightGroup | null
}) {
  const { ratebook, visible, onClose, onSuccess, editingBaseFreight } = props

  const {
    form,
    hasEndDate,
    handleHasEndDateChange,
    handleSubmit,
    metadataOptions,
    baseFreightRatesResponse,
    isLoading,
  } = useBaseFreightDrawer({
    ratebook: ratebook as Ratebook,
    visible,
    onClose,
    onSuccess,
    editingBaseFreight,
  })

  return (
    <Drawer
      title={editingBaseFreight ? 'Edit Freight Table' : 'Create Freight Table'}
      open={visible}
      onClose={onClose}
      width={400}
      styles={{
        body: {
          padding: 0,
        },
      }}
      footer={
        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton appearance='outlined' onClick={onClose}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 onClick={() => form.submit()} loading={isLoading}>
            {editingBaseFreight ? 'Update' : 'Create'}
          </GraviButton>
        </Horizontal>
      }
    >
      <Form
        form={form}
        layout='vertical'
        style={{ backgroundColor: 'var(--bg-1)', height: '100%' }}
        onFinish={handleSubmit}
        initialValues={{
          effective_from: dayjs(),
        }}
      >
        <Vertical className='p-3' bg='default' gap={16}>
          <Form.Item
            name='region'
            extra={
              metadataOptions?.freight_regions?.length === 0 &&
              "No regions available - assign customer to the 'freight customer' field on sites to populate."
            }
            label='Region'
          >
            <Select
              allowClear
              placeholder='Any'
              options={metadataOptions?.freight_regions
                ?.map((region: Region) => ({
                  label: region.name,
                  value: region.name,
                  region_id: region.id,
                }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
              onChange={(_, option) => {
                if (!Array.isArray(option)) {
                  form.setFieldValue('region_id', option?.region_id)
                }
              }}
            />
          </Form.Item>

          <Form.Item name='product_group' label='Product Group'>
            <Select
              allowClear
              placeholder='Any'
              options={metadataOptions?.product_groups
                ?.map((product: string) => ({ label: product, value: product }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
            />
          </Form.Item>

          <Form.Item name='unit' label='Rate Type' rules={[{ required: true, message: 'Rate Type is required' }]}>
            <Select
              options={metadataOptions?.ratebook_options?.rate_units?.map((unit: string) => ({
                label: unit,
                value: unit,
              }))}
            />
          </Form.Item>

          {!editingBaseFreight && (
            <>
              <Form.Item
                name='effective_from'
                label='Effective From'
                rules={[
                  {
                    required: true,
                    message: 'Please select an effective date',
                  },
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name='copyFrom' label='Copy From'>
                <Select
                  allowClear
                  placeholder='Select a base freight to copy from'
                  options={baseFreightRatesResponse?.rows?.map((group) => ({
                    label: `${group.freight_region_name || 'Any'} - ${group.product_group || 'Any'}`,
                    value: `${group.freight_region_name || ''}-${group.product_group || ''}`,
                    region: group.freight_region_name,
                    product_group: group.product_group,
                  }))}
                />
              </Form.Item>

              <Horizontal verticalCenter gap={8} style={{ marginBottom: 16 }}>
                <Switch checked={hasEndDate} onChange={handleHasEndDateChange} />
                <Texto>{hasEndDate ? 'Set End Date' : 'No End Date'}</Texto>
              </Horizontal>

              {hasEndDate && (
                <Form.Item
                  name='effective_to'
                  label='Effective To'
                  rules={[
                    { required: true, message: 'Please select an end date' },
                    {
                      validator: (_, value) => {
                        const effectiveFrom = form.getFieldValue('effective_from')
                        if (value && effectiveFrom && value.isBefore(effectiveFrom)) {
                          return Promise.reject('End date must be after start date')
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              )}
            </>
          )}
        </Vertical>
      </Form>
    </Drawer>
  )
}
